import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Table,
  DatePicker,
  Space,
  Card,
  Statistic,
  Row,
  Col,
  Tag,
  Typography,
  Spin,
  Alert,
  Button
} from 'antd';
import { ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCompany } from '../../../contexts/CompanyContext';
import { useSecureRequest } from '../../../hooks/useSecureRequest';
import { PartnerLedgerResponse, PartnerLedgerMovement } from '../../../services/accountingService';
import { formatCurrency } from '../journal-entries/journalEntriesHelpers';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface PartnerLedgerDrawerProps {
  open: boolean;
  onClose: () => void;
  contactId: number | null;
  contactName: string;
}

export const PartnerLedgerDrawer: React.FC<PartnerLedgerDrawerProps> = ({
  open,
  onClose,
  contactId,
  contactName,
}) => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [ledgerData, setLedgerData] = useState<PartnerLedgerResponse | null>(null);

  const fetchLedger = useCallback(async () => {
    if (!contactId || (!company.current?.id && !company.current?.uid)) return;

    try {
      const res = await securePost('/accounting/reports/partner-ledger', {
        company_id: company.current?.id,
        company: company.current?.uid || company.current?.id,
        contact_id: contactId,
        start_date: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      });

      if (res?.data) {
        setLedgerData(res.data);
      }
    } catch (err) {
      console.error('Error fetching partner ledger:', err);
    }
  }, [contactId, company.current?.id, company.current?.uid, dateRange, securePost]);

  useEffect(() => {
    if (open && contactId) {
      void fetchLedger();
    } else {
      setLedgerData(null);
    }
  }, [open, contactId, fetchLedger]);

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'entry_date',
      key: 'entry_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Comprobante',
      dataIndex: 'entry_number',
      key: 'entry_number',
      width: 140,
      render: (num: string) => (
        <Space size={4}>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <Text strong style={{ color: '#1890ff' }}>{num}</Text>
        </Space>
      ),
    },
    {
      title: 'Cuenta PUC',
      dataIndex: 'account_code',
      key: 'account_code',
      width: 160,
      render: (code: string, r: PartnerLedgerMovement) => (
        <Space direction="vertical" size={0}>
          <Tag color={code.startsWith('2355') ? 'blue' : 'orange'}>{code}</Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.account_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Concepto / Detalle',
      dataIndex: 'concept',
      key: 'concept',
      render: (c: string, r: PartnerLedgerMovement) => (
        <div>
          <div>{c}</div>
          {r.line_description && r.line_description !== c && (
            <Text type="secondary" style={{ fontSize: 11 }}>{r.line_description}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Débito (Pagos empresa)',
      dataIndex: 'debit',
      key: 'debit',
      align: 'right' as const,
      width: 150,
      render: (val: number) => (
        <Text style={{ color: val > 0 ? '#fa8c16' : '#bfbfbf' }}>
          {val > 0 ? formatCurrency(val) : '-'}
        </Text>
      ),
    },
    {
      title: 'Crédito (Aportes/Gastos socio)',
      dataIndex: 'credit',
      key: 'credit',
      align: 'right' as const,
      width: 150,
      render: (val: number) => (
        <Text style={{ color: val > 0 ? '#52c41a' : '#bfbfbf' }}>
          {val > 0 ? formatCurrency(val) : '-'}
        </Text>
      ),
    },
    {
      title: 'Saldo Acumulado',
      dataIndex: 'running_balance',
      key: 'running_balance',
      align: 'right' as const,
      width: 160,
      render: (val: number) => {
        const isPositive = (val || 0) >= 0;
        return (
          <Text strong style={{ color: isPositive ? '#52c41a' : '#f5222d' }}>
            {formatCurrency(val || 0)}
          </Text>
        );
      },
    },
  ];

  const finalBalance = ledgerData?.final_balance || 0;

  return (
    <Drawer
      title={
        <div>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            Extracto de Cuenta Corriente: {contactName}
          </span>
          <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 'normal' }}>
            Movimientos auxiliares de Deudas con Socios (2355) y Cuentas por Cobrar a Socios (1325)
          </div>
        </div>
      }
      placement="right"
      width={980}
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as any)}
            format="DD/MM/YYYY"
            placeholder={['Fecha Inicial', 'Fecha Final']}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchLedger} loading={loading}>
            Filtrar
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={6}>
            <Card size="small" style={{ background: '#fafafa' }}>
              <Statistic
                title="Saldo Inicial"
                value={ledgerData?.initial_balance || 0}
                formatter={(val) => formatCurrency(Number(val))}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small" style={{ background: '#fff7e6' }}>
              <Statistic
                title="Total Débitos (Pagos)"
                value={ledgerData?.total_debit || 0}
                formatter={(val) => formatCurrency(Number(val))}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small" style={{ background: '#f6ffed' }}>
              <Statistic
                title="Total Créditos (Gastos asumidos)"
                value={ledgerData?.total_credit || 0}
                formatter={(val) => formatCurrency(Number(val))}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              size="small"
              bordered
              style={{
                background: finalBalance >= 0 ? '#f6ffed' : '#fff1f0',
                borderColor: finalBalance >= 0 ? '#b7eb8f' : '#ffa39e',
              }}
            >
              <Statistic
                title="Saldo Final Neto"
                value={finalBalance}
                formatter={(val) => formatCurrency(Number(val))}
                valueStyle={{ color: finalBalance >= 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>
                {finalBalance >= 0 ? 'Empresa debe al socio' : 'Socio debe a la empresa'}
              </Text>
            </Card>
          </Col>
        </Row>

        <Alert
          message="Convención de signos en Cuenta Corriente de Socios"
          description="Saldo positivo (+): La empresa le adeuda al socio por compras o gastos personales asumidos a favor de la empresa. Saldo negativo (-): El socio adeuda a la empresa por anticipos de utilidades o retiros personales."
          type="info"
          showIcon
          style={{ marginBottom: 16, fontSize: 12 }}
        />

        <Table
          rowKey={(r, idx) => `${r.entry_id}-${idx}`}
          columns={columns}
          dataSource={ledgerData?.movements || []}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          size="small"
          locale={{ emptyText: 'No hay movimientos contables registrados para este socio' }}
        />
      </Spin>
    </Drawer>
  );
};

export default PartnerLedgerDrawer;